import React, {useEffect, useRef, useState} from 'react';

import {addToast, Icon, Label, Text, Toggle} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import RestrictFreePracticeModal from 'client/Classrooms/Classroom/Teacher/Subjects/RestrictFreePracticeModal.react';
import {useUpdateClassroomSubjectAccessMutation} from 'gql/shared/ClassroomSubjectAccess.queries.generated';
import classNames from 'classnames';
import withTracking from 'lib/hocs/withTracking';
import {Link} from 'react-router';

const TrackedToggle = withTracking(Toggle, {
  onClick: {
    event: 'click',
    type: 'toggle',
    feature: 'Course Library',
    component: 'Practice Settings Menu',
    content: (e: React.ChangeEvent<HTMLInputElement>) => (e.target.checked ? 'Open' : null)
  }
});

interface FreePracticeOptionToggleProps {
  classroom: ClassroomModelV1;
  defaultChecked: boolean;
  subjectId: string;
  disabled?: boolean;
}

const FreePracticeOptionToggle = ({
  defaultChecked,
  classroom,
  subjectId,
  disabled = false
}: FreePracticeOptionToggleProps) => {
  const [showModal, setShowModal] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [updateClassroomSubjectAccess] = useUpdateClassroomSubjectAccessMutation();

  const inputRef = useRef<HTMLInputElement>(null);

  // Prevent Menu's WithToggle from closing the dropdown when clicking inside
  // the confirmation modal. The modal renders in a portal (#portal) outside
  // the Menu wrapper, so WithToggle's document-level mousedown listener would
  // otherwise detect it as a click-outside event. Stopping mousedown
  // propagation at #portal keeps click events working normally for the modal.
  useEffect(() => {
    if (!showModal) return;

    const portalElement = global.document.getElementById('portal');
    if (!portalElement) return;

    const stopMouseDown = (e: MouseEvent) => e.stopPropagation();
    portalElement.addEventListener('mousedown', stopMouseDown, true);

    return () => {
      portalElement.removeEventListener('mousedown', stopMouseDown, true);
    };
  }, [showModal]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      try {
        setIsRequestPending(true);
        await updateClassroomSubjectAccess({
          variables: {
            classroomId: classroom.getId(),
            subjectId: subjectId,
            input: {restrictFreePractice: false}
          }
        });
        addToast({
          message: 'Your changes have been saved.',
          title: 'Success!',
          color: 'positive'
        });
      } catch {
        addToast({
          message:
            'We encountered an error while saving your changes. Please contact us if the problem persists.',
          title: 'Something went wrong',
          color: 'negative'
        });
        if (inputRef.current) {
          inputRef.current.checked = false;
        }
      }
      setIsRequestPending(false);
    } else {
      setShowModal(true);
    }
  };

  const handleModalConfirm = async () => {
    try {
      setIsRequestPending(true);
      await updateClassroomSubjectAccess({
        variables: {
          classroomId: classroom.getId(),
          subjectId: subjectId,
          input: {restrictFreePractice: true}
        }
      });
      addToast({
        message: 'Your changes have been saved.',
        title: 'Success!',
        color: 'positive'
      });
    } catch {
      addToast({
        message:
          'We encountered an error while saving your changes. Please contact us if the problem persists.',
        title: 'Something went wrong',
        color: 'negative'
      });
      if (inputRef.current) {
        inputRef.current.checked = true;
      }
    }
    setIsRequestPending(false);
  };

  return (
    <>
      <Label
        className={classNames('flex w-full items-center justify-between', {
          'cursor-pointer': !disabled
        })}
      >
        <Text as='div' className='mr-auto' color='secondary' bold size='m'>
          {classroom.getName()}
        </Text>
        <TrackedToggle
          className='ml-x2'
          defaultChecked={defaultChecked}
          disabled={disabled || isRequestPending || showModal}
          onChange={handleChange}
          ref={inputRef}
        />
      </Label>
      {disabled && (
        <div>
          <Text color='tertiary' italic size='xs'>
            <Link
              to={`/classes/${classroom.getId()}/settings/details`}
              className='whitespace-nowrap content-tertiary no-underline hover:underline'
            >
              Requires license access
              <Icon icon='arrow-up-right' iconStyle='regular' className='ml-x1' />
            </Link>
          </Text>
        </div>
      )}
      {showModal ? (
        <RestrictFreePracticeModal
          classroomId={classroom.getId()}
          onCancel={() => {
            if (inputRef.current) {
              inputRef.current.checked = true;
            }
          }}
          handleClose={() => {
            setShowModal(false);
          }}
          onRestrict={handleModalConfirm}
        />
      ) : null}
    </>
  );
};

export default FreePracticeOptionToggle;
